import React, { useState, useEffect } from 'react';
import { reviewAgreement } from '../../utils/reviewAgreement';
import { submissionStatusLabel } from '../../constants/submissionStatus';
import DecisionDialog, { DECISION_OUTCOMES } from '../../components/decisions/DecisionDialog';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import SortableTh, { useSort } from '../../components/ui/SortableTh';
import useTableFilters from '../../components/ui/useTableFilters';
import ActiveFilters from '../../components/ui/ActiveFilters';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { conferenceService } from '../../services/conferenceService';
import { profileService } from '../../services/profileService';
import { notificationService } from '../../services/notificationService';
import { reviewCriteriaService } from '../../services/reviewCriteriaService';
import assignmentService from '../../services/assignmentService';
import decisionService from '../../services/decisionService';
import { renderEmailEvent } from '../../services/emailTemplates';
import { getDecisionLock } from '../../lib/decisionLock';
import { submissionService } from '../../services/submissionService';
import ProgramTab from './components/ProgramTab';
import EmailTemplatesTab from './components/EmailTemplatesTab';
import AnnouncementsTab from './components/AnnouncementsTab';
import BadgesTab from './components/BadgesTab';
import ReviewerSearch from './components/ReviewerSearch';
import { formatDate, formatDateTime } from '../../utils/formatters';

const addWorksheetHyperlinks = (XLSX, worksheet, rows, headers, linksByColumn) => {
  Object.entries(linksByColumn || {})?.forEach(([columnName, urls]) => {
    const columnIndex = headers?.indexOf(columnName);
    if (columnIndex === -1) return;

    rows?.forEach((_, rowIndex) => {
      const url = urls?.[rowIndex];
      if (!url) return;

      const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex });
      // Use Excel HYPERLINK formula — more reliable than cell.l across Excel, Google Sheets, LibreOffice
      worksheet[cellRef] = {
        t: 'str',
        f: `HYPERLINK("${url}","Download file")`,
        v: 'Download file'
      };
    });
  });
};

const ConferenceManagementDashboard = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError, info: toastInfo } = useToast();
  const { t } = useTranslation('conferences');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Sorting for the submissions table. Default newest-first: a chair scanning a
  // conference cares most about what has just arrived.
  const { sort: subSort, toggle: toggleSubSort, sortRows: sortSubmissions } = useSort('submittedAt', 'desc');
  // Accepts both spellings. The app used ?id= on six routes and
  // ?conferenceId= on three, so links between them only worked if you knew
  // which one each page wanted. Everything now *emits* ?conferenceId=;
  // reading ?id= as well keeps already-shared and bookmarked links alive.
  const conferenceId = searchParams?.get('conferenceId') || searchParams?.get('id');
  const { user } = useAuth();
  // Which section is open is now part of the URL (?tab=), not private state.
  // The conference bar above is the only navigation for a conference, so it has
  // to be able to address these sections — and a chair can bookmark or share
  // "the team tab of ICACDS" instead of describing how to click to it.
  const activeTab = searchParams?.get('tab') || 'submissions';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'submissions') next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const [conference, setConference] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionFilter, setSubmissionFilter] = useState('all');
  
  // New state for paper management
  const [selectedPapers, setSelectedPapers] = useState([]);
  const [showReviewerModal, setShowReviewerModal] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [assigningReviewer, setAssigningReviewer] = useState(false);
  const [processingDecision, setProcessingDecision] = useState(null);
  // Which paper/outcome the decision dialog is confirming, if any.
  const [pendingDecision, setPendingDecision] = useState(null);
  // The conference's own wording for each automated email, keyed by event.
  // Without this the dialog would preview the built-in default while a
  // customised template actually went out.
  const [eventTemplates, setEventTemplates] = useState({});
  const openDecision = (paper, outcome) => setPendingDecision({ paper, outcome });
  // Bulk uses the same dialog, given a list instead of one paper.
  const [pendingBulk, setPendingBulk] = useState(null);

  const decisionLockMessage = (lock) => {
    if (lock?.reason === 'reviews') {
      return t('managementDashboard.decisions.lockedReviews', {
        done: lock.done, total: lock.total, date: formatDate(lock.until),
        defaultValue: 'Decision opens once all reviews are in ({{done}}/{{total}} done), or after the submission deadline ({{date}}).',
      });
    }
    return t('managementDashboard.decisions.lockedDeadline', {
      date: formatDate(lock?.until),
      defaultValue: 'Decision opens after the submission deadline ({{date}}).',
    });
  };

  // Withdraw only applies to accepted papers; every other outcome only to
  // papers that are ready (see lib/decisionLock — the DB trigger enforces the
  // same rule, this just keeps the dialog from promising what it can't do).
  const bulkPapersFor = (outcome) => (submissions || [])
    .filter((x) => selectedPapers?.includes(x?.id))
    .filter((p) => (outcome?.key === 'withdraw'
      ? p?.status === 'accepted'
      : !getDecisionLock(p, conference)));

  const openBulk = (outcome) => {
    if (!selectedPapers?.length) { toastError(t('managementDashboard.decisions.selectFirst', 'Select at least one paper first.')); return; }
    setShowBulkActionsDropdown(false);
    const eligible = bulkPapersFor(outcome);
    const skipped = selectedPapers.length - eligible.length;
    if (eligible.length === 0) {
      toastError(outcome?.key === 'withdraw'
        ? t('managementDashboard.decisions.bulkNoneAccepted', 'None of the selected papers are accepted, so there is nothing to withdraw.')
        : t('managementDashboard.decisions.bulkNoneReady', 'None of the selected papers can be decided yet — they are still waiting on reviews or on the submission deadline.'));
      return;
    }
    if (skipped > 0) {
      toastInfo(t('managementDashboard.decisions.bulkSomeSkipped', {
        count: skipped,
        defaultValue: '{{count}} selected paper(s) left out — not ready for this decision yet.',
      }));
    }
    setPendingBulk(outcome);
  };
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [showBulkActionsDropdown, setShowBulkActionsDropdown] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [processingBulkDecision, setProcessingBulkDecision] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState(false);
  const [deletingSubmission, setDeletingSubmission] = useState(null);

  const [editForm, setEditForm] = useState({
    name: '',
    shortName: '',
    description: '',
    locationType: '',
    venueName: '',
    location: '',
    startDate: '',
    endDate: '',
    paperDeadline: '',
    reviewDeadline: '',
    decisionDate: '',
    cameraReadyDeadline: '',
    status: '',
    allowedFileTypes: 'both',
    callForPapersUrl: '',
    bannerImageUrl: ''
  });

  const [newTrack, setNewTrack] = useState({ name: '', description: '' });
  const [newKeyword, setNewKeyword] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [cfpFile, setCfpFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  // The signed URL of the banner already saved on the conference. Loaded from
  // storage further down; nothing to revoke.
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState(null);
  // Separately: a preview of a file the organiser has just picked but not saved.
  // One blob URL per file, revoked on replace and unmount — building it inline
  // in render minted a new URL on every keystroke elsewhere in the form.
  const [bannerObjectUrl, setBannerObjectUrl] = useState(null);
  useEffect(() => {
    if (!bannerFile) { setBannerObjectUrl(null); return undefined; }
    const url = URL.createObjectURL(bannerFile);
    setBannerObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [bannerFile]);
  const [cfpUploading, setCfpUploading] = useState(false);
  const [cfpDownloadUrl, setCfpDownloadUrl] = useState(null);

  // Team management state
  const [teamMembers, setTeamMembers] = useState({ chairs: [], organizers: [], reviewers: [] });
  const [addingMember, setAddingMember] = useState(false);
  const [newMemberRole, setNewMemberRole] = useState('chair');
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
    } else if (conferenceId) {
      // Don't reset the tab here any more: the URL owns it, and clobbering it
      // on load would fight the conference bar that just set it.
      setSubmissionFilter('all');
      setSelectedPapers([]);
      setExpandedSubmission(null);
      setOpenDropdown(null);
      setShowBulkActionsDropdown(false);
      loadConferenceData();
      notificationService?.getEventTemplates?.(conferenceId)
        ?.then((tpls) => setEventTemplates(tpls || {}))
        ?.catch(() => setEventTemplates({}));
    } else {
      // No ?id= in the URL — nothing to load. Without this the page sat on its
      // spinner forever (loading starts true and only loadConferenceData clears
      // it), so a bookmarked or shared link looked like a broken app.
      setLoading(false);
      setError('missing-conference-id');
    }
  }, [user, conferenceId, navigate]);

  // Populate editForm when conference data is loaded
  useEffect(() => {
    if (conference) {
      const toDate = (v) => v ? v.split('T')[0] : '';
      setEditForm({
        name: conference?.name || '',
        shortName: conference?.shortName || '',
        description: conference?.description || '',
        locationType: conference?.locationType || '',
        venueName: conference?.venueName || '',
        location: conference?.location || '',
        startDate: toDate(conference?.startDate),
        endDate: toDate(conference?.endDate),
        paperDeadline: toDate(conference?.paperDeadline),
        reviewDeadline: toDate(conference?.reviewDeadline),
        decisionDate: toDate(conference?.decisionDate),
        cameraReadyDeadline: toDate(conference?.cameraReadyDeadline),
        status: conference?.status || '',
        allowedFileTypes: conference?.allowedFileTypes || 'both',
        callForPapersUrl: conference?.callForPapersUrl || '',
        bannerImageUrl: conference?.bannerImageUrl || ''
      });

      // Load banner image preview URL
      if (conference?.bannerImageUrl) {
        conferenceService?.getBannerImageUrl(conference?.bannerImageUrl)?.then(url => {
          setBannerPreviewUrl(url);
        })?.catch(() => setBannerPreviewUrl(null));
      } else {
        setBannerPreviewUrl(null);
      }
    }
  }, [conference]);

  // Real-time subscription for paper status changes
  useEffect(() => {
    if (!conferenceId) return;

    // Subscribe to changes in conference_submissions table for this conference
    const submissionsChannel = supabase?.channel(`conference-submissions-${conferenceId}`)?.on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'conference_submissions',
          filter: `conference_id=eq.${conferenceId}`
        },
        async (payload) => {
          console.log('Real-time submission change detected:', payload);
          
          // Reload submissions data when any change occurs
          try {
            let submissionsData = await conferenceService?.getSubmissions(conferenceId);
            setSubmissions(submissionsData);
            
            // Show a subtle notification to user
            if (payload?.eventType === 'UPDATE') {
              const oldStatus = payload?.old?.status;
              const newStatus = payload?.new?.status;
              if (oldStatus !== newStatus) {
                console.log(`Paper status changed from ${oldStatus} to ${newStatus}`);
              }
            }
          } catch (err) {
            console.error('Error reloading submissions after real-time update:', err);
          }
        }
      )?.subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase?.removeChannel(submissionsChannel);
    };
  }, [conferenceId]);

  const loadConferenceData = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      console.log('Loading conference data for ID:', conferenceId);
      console.log('Current user:', user?.id);
      
      // Load conference data first
      const confData = await conferenceService?.getById(conferenceId);
      console.log('Conference data loaded:', confData);
      
      if (!confData) {
        throw new Error('Conference not found');
      }
      
      setConference(confData);
      setKeywords(confData?.keywords || []);
      
      // Check user's role in this conference
      try {
        const { data: userRoles, error: rolesError } = await supabase
          ?.from('conference_roles')
          ?.select('role')
          ?.eq('conference_id', conferenceId)
          ?.eq('user_id', user?.id);
        
        console.log('👤 User roles in this conference:', userRoles);
        if (rolesError) console.error('Error fetching user roles:', rolesError);
      } catch (roleCheckError) {
        console.error('Role check failed:', roleCheckError);
      }
      
      // Load tracks and submissions separately with individual error handling
      let tracksData = [];
      let submissionsData = [];
      
      try {
        tracksData = await conferenceService?.getTracks(conferenceId);
        console.log('Tracks loaded:', tracksData?.length);
      } catch (tracksError) {
        console.error('Error loading tracks:', tracksError);
        // Continue even if tracks fail
      }
      
      try {
        submissionsData = await conferenceService?.getSubmissions(conferenceId);
        console.log('📦 Submissions loaded in component:', submissionsData?.length);
        if (submissionsData?.length === 0) {
          console.warn('⚠️ No submissions returned - checking database directly...');
          
          // Direct database check
          const { data: directCheck, error: directError } = await supabase
            ?.from('conference_submissions')
            ?.select('id, title, status')
            ?.eq('conference_id', conferenceId);
          
          console.log('🔍 Direct database check:', { 
            count: directCheck?.length, 
            data: directCheck,
            error: directError 
          });
        }
      } catch (submissionsError) {
        console.error('Error loading submissions:', submissionsError);
        // Continue even if submissions fail
      }
      
      setTracks(tracksData);
      setSubmissions(submissionsData);

      // Load team members
      try {
        const teamData = await conferenceService?.getConferenceTeamMembers(conferenceId);
        // Resolve avatar URLs
        const resolveAvatars = async (members) => {
          return Promise.all(members?.map(async (member) => {
            let avatarSignedUrl = null;
            if (member?.avatarUrl) {
              try {
                avatarSignedUrl = await profileService?.getAvatarUrl(member?.avatarUrl);
              } catch (e) { /* ignore */ }
            }
            return { ...member, avatarSignedUrl };
          }));
        };
        const chairs = await resolveAvatars(teamData?.chairs || []);
        const organizers = await resolveAvatars(teamData?.organizers || []);
        const reviewers = await resolveAvatars(teamData?.reviewers || []);
        setTeamMembers({ chairs, organizers, reviewers });

        // Load pending invitations
        const invitations = await conferenceService?.getPendingInvitations(conferenceId);
        setPendingInvitations(invitations || []);
      } catch (teamError) {
        console.error('Error loading team members:', teamError);
      }

      // Load review statistics
      if (confData?.id) {
        try {
          const stats = await reviewCriteriaService?.getConferenceReviewStats(confData?.id);
          setReviewStats(stats);
        } catch (statsError) {
          console.error('Error loading review stats:', statsError);
          // Continue even if stats fail
        }
      }
    } catch (err) {
      console.error('Error loading conference data:', err);
      setError(err?.message || 'Failed to load conference data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);

      // Upload banner image if new file selected
      let bannerUrl = editForm?.bannerImageUrl;
      if (bannerFile) {
        if (editForm?.bannerImageUrl) {
          await conferenceService?.deleteBannerImage(editForm?.bannerImageUrl);
        }
        bannerUrl = await conferenceService?.uploadBannerImage(bannerFile, conferenceId);
      }

      // Upload CFP file if new file selected
      let cfpUrl = editForm?.callForPapersUrl;
      if (cfpFile) {
        if (editForm?.callForPapersUrl) {
          await conferenceService?.deleteCallForPapersPDF(editForm?.callForPapersUrl);
        }
        cfpUrl = await conferenceService?.uploadCallForPapersPDF(cfpFile, conferenceId);
      }

      await conferenceService?.update(conferenceId, {
        ...editForm,
        bannerImageUrl: bannerUrl,
        callForPapersUrl: cfpUrl
      });
      await loadConferenceData();
      setCfpFile(null);
      setBannerFile(null);
      toastSuccess(t('managementDashboard.settings.settingsUpdated'));
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCfpFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      if (file?.type !== 'application/pdf') {
        toastError(t('managementDashboard.settings.callForPapers.onlyPdf'));
        return;
      }
      // Validate file size (max 10MB)
      if (file?.size > 10 * 1024 * 1024) {
        toastError(t('managementDashboard.settings.callForPapers.maxSize'));
        return;
      }
      setCfpFile(file);
    }
  };

  const handleRemoveCfpFile = () => {
    setCfpFile(null);
  };

  const handleDeleteCfpDocument = async () => {
    if (!window.confirm(t('managementDashboard.settings.callForPapers.deleteConfirm'))) {
      return;
    }

    try {
      setSaving(true);
      if (editForm?.callForPapersUrl) {
        await conferenceService?.deleteCallForPapersPDF(editForm?.callForPapersUrl);
      }
      await conferenceService?.update(conferenceId, {
        ...editForm,
        callForPapersUrl: null
      });
      await loadConferenceData();
      toastSuccess(t('managementDashboard.settings.callForPapers.deleteSuccess'));
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCfp = async () => {
    try {
      setCfpUploading(true);
      const url = await conferenceService?.getCallForPapersPDFUrl(editForm?.callForPapersUrl);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    } finally {
      setCfpUploading(false);
    }
  };

  const handleBannerFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file?.type)) {
        toastError(t('managementDashboard.settings.imageTypeError'));
        return;
      }
      if (file?.size > 5 * 1024 * 1024) {
        toastError(t('managementDashboard.settings.imageSizeError'));
        return;
      }
      setBannerFile(file);
    }
  };

  const handleRemoveBannerFile = () => {
    setBannerFile(null);
  };

  const handleDeleteBannerImage = async () => {
    if (!window.confirm(t('managementDashboard.settings.bannerDeleteConfirm'))) {
      return;
    }

    try {
      setSaving(true);
      if (editForm?.bannerImageUrl) {
        await conferenceService?.deleteBannerImage(editForm?.bannerImageUrl);
      }
      await conferenceService?.update(conferenceId, {
        ...editForm,
        bannerImageUrl: null
      });
      setBannerPreviewUrl(null);
      await loadConferenceData();
      toastSuccess(t('managementDashboard.settings.bannerDeleteSuccess'));
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleManageReviewSchema = () => {
    navigate(`/review-schema-builder?conferenceId=${conferenceId}`);
  };

  const handleViewSubmissionTracking = () => {
    navigate(`/conference-submission-tracking?conferenceId=${conferenceId}`);
  };

  const handleExportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const { saveAs } = await import('file-saver');

      const dataToExport = getFilteredSubmissions();
      if (!dataToExport || dataToExport.length === 0) {
        toastError(t('managementDashboard.submissions.noSubmissionsToExport'));
        return;
      }

      // 315360000 seconds = ~10 years — permanent for all practical purposes
      const signedFileUrls = await submissionService?.getSubmissionFileUrls(
        dataToExport?.flatMap(sub => [sub?.pdfUrl, sub?.cameraReadyUrl]),
        315360000
      );

      const exportEntries = dataToExport.map((sub, idx) => {
        const completedReviews = sub?.reviewers?.filter(r => r?.reviewStatus === 'completed') || [];
        const avgScore = completedReviews.length > 0
          ? (completedReviews.reduce((sum, r) => sum + (r?.overallScore || 0), 0) / completedReviews.length).toFixed(2)
          : '';
        const submissionDownloadUrl = signedFileUrls?.get(sub?.pdfUrl) || '';
        const cameraReadyDownloadUrl = signedFileUrls?.get(sub?.cameraReadyUrl) || '';

        return {
          row: {
            'Paper #': sub?.paperNumber ?? '',
            'Paper ID': sub?.id || '',
            'Title': sub?.title || '',
            'Abstract': sub?.abstract || '',
            'Keywords': Array.isArray(sub?.keywords) ? sub.keywords.join(', ') : (sub?.keywords || ''),
            'Track': sub?.trackName || '',
            'Status': submissionStatusLabel(sub?.status, t),
            'Author First Name': sub?.author?.firstName || '',
            'Author Last Name': sub?.author?.lastName || '',
            'Author Email': sub?.author?.email || '',
            'Author Institution': sub?.author?.institution || '',
            'Co-Authors': Array.isArray(sub?.coAuthors)
              ? sub.coAuthors.map(ca => `${ca?.firstName || ''} ${ca?.lastName || ''} (${ca?.email || ''}${ca?.institution ? ', ' + ca.institution : ''})`).join('; ')
              : '',
            'Submission File': sub?.pdfFileName || '',
            'Submission Download': submissionDownloadUrl ? 'Download file' : '',
            'Submitted At': formatDateTime(sub?.submittedAt),
            'Last Updated': formatDateTime(sub?.updatedAt),
            'Reviewers': sub?.reviewers?.map(r => `${r?.firstName || ''} ${r?.lastName || ''}`).join('; ') || '',
            'Avg Review Score': avgScore,
            'Review Decisions': completedReviews.map(r => r?.decision || '').filter(Boolean).join('; '),
            'Camera-Ready File': sub?.cameraReadyFileName || '',
            'Camera-Ready Download': cameraReadyDownloadUrl ? 'Download file' : '',
            'Camera-Ready Date': formatDateTime(sub?.cameraReadySubmittedAt),
          },
          submissionDownloadUrl,
          cameraReadyDownloadUrl
        };
      });

      const rows = exportEntries.map(entry => entry?.row);
      const headers = Object.keys(rows?.[0] || {});

      const worksheet = XLSX.utils.json_to_sheet(rows);
      addWorksheetHyperlinks(XLSX, worksheet, rows, headers, {
        'Submission Download': exportEntries.map(entry => entry?.submissionDownloadUrl),
        'Camera-Ready Download': exportEntries.map(entry => entry?.cameraReadyDownloadUrl)
      });

      // Auto-size columns
      const colWidths = headers.map(key => {
        const maxLen = Math.max(
          key.length,
          ...rows.map(r => String(r[key] || '').length)
        );
        return { wch: Math.min(maxLen + 2, 60) };
      });
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `${conference?.shortName || conference?.name || 'conference'}_submissions_${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(blob, fileName);
    } catch (err) {
      console.error('Export error:', err);
      toastError(t('managementDashboard.submissions.exportFailed'));
    }
  };

  // kind: 'package' (paper + camera-ready + permission form, the historical
  // "download everything" bundle), 'camera_ready', or 'permission_form'.
  const handleDownloadSelectedFiles = async (kind = 'package') => {
    if (selectedPapers?.length === 0) return;
    setDownloadingFiles(true);
    try {
      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');

      const filteredSubs = getFilteredSubmissions();
      const selected = filteredSubs.filter(sub => selectedPapers.includes(sub.id));

      const includePaper = kind === 'package';
      const includeCameraReady = kind === 'package' || kind === 'camera_ready';
      const includePermissionForm = kind === 'package' || kind === 'permission_form';

      const filePaths = selected.flatMap(sub => [
        includePaper ? sub.pdfUrl : null,
        includeCameraReady ? sub.cameraReadyUrl : null,
        includePermissionForm ? sub.permissionFormUrl : null,
      ]).filter(Boolean);
      if (filePaths.length === 0) {
        toastError('No files available for the selected submissions.');
        return;
      }

      const signedUrls = await submissionService?.getSubmissionFileUrls(filePaths, 3600);
      const zip = new JSZip();
      let count = 0;
      const missingCameraReady = [];
      const missingPermissionForm = [];

      for (const sub of selected) {
        const label = sub.paperNumber ? `#${sub.paperNumber}` : (sub.title || sub.id);

        if (includePaper && sub.pdfUrl) {
          const url = signedUrls?.get(sub.pdfUrl);
          if (url) {
            try {
              const blob = await fetch(url).then(r => r.blob());
              zip.file(sub.pdfFileName || `submission_${sub.id}.pdf`, blob);
              count++;
            } catch (e) { console.error('Failed to fetch', sub.pdfFileName, e); }
          }
        }
        if (includeCameraReady) {
          if (sub.cameraReadyUrl) {
            const url = signedUrls?.get(sub.cameraReadyUrl);
            if (url) {
              try {
                const blob = await fetch(url).then(r => r.blob());
                zip.file(`camera_ready_${sub.cameraReadyFileName || sub.id + '.pdf'}`, blob);
                count++;
              } catch (e) { console.error('Failed to fetch camera-ready for', sub.id, e); }
            }
          } else {
            missingCameraReady.push(label);
          }
        }
        if (includePermissionForm) {
          if (sub.permissionFormUrl) {
            const url = signedUrls?.get(sub.permissionFormUrl);
            if (url) {
              try {
                const blob = await fetch(url).then(r => r.blob());
                zip.file(`permission_form_${sub.permissionFormFileName || sub.id + '.pdf'}`, blob);
                count++;
              } catch (e) { console.error('Failed to fetch permission form for', sub.id, e); }
            }
          } else {
            missingPermissionForm.push(label);
          }
        }
      }

      if (count === 0) {
        toastError('Could not retrieve any files. Links may have expired — try again.');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const suffix = kind === 'camera_ready' ? 'camera_ready' : kind === 'permission_form' ? 'permission_forms' : 'papers';
      const zipName = `${conference?.shortName || conference?.name || 'conference'}_${suffix}_${new Date().toISOString().slice(0, 10)}.zip`;
      saveAs(zipBlob, zipName);

      // Let the chair know who got left out of the zip, rather than them
      // noticing only when they count the files inside it.
      const formatMissing = (labels) => labels.length <= 5
        ? labels.join(', ')
        : `${labels.slice(0, 5).join(', ')} and ${labels.length - 5} more`;
      const missingParts = [];
      if (missingCameraReady.length > 0) missingParts.push(`camera-ready missing for ${formatMissing(missingCameraReady)}`);
      if (missingPermissionForm.length > 0) missingParts.push(`permission form missing for ${formatMissing(missingPermissionForm)}`);
      if (missingParts.length > 0) toastInfo(`Downloaded, but ${missingParts.join('; ')}.`);
    } catch (err) {
      console.error('Download error:', err);
      toastError('Failed to download files. Please try again.');
    } finally {
      setDownloadingFiles(false);
    }
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowSubmissionModal(true);
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    setDeletingSubmission(submissionId);
    try {
      await submissionService?.delete(submissionId);
      setSubmissions(prev => prev.filter(s => s?.id !== submissionId));
      if (expandedSubmission === submissionId) setExpandedSubmission(null);
    } catch (err) {
      console.error('Error deleting submission:', err);
      toastError('Failed to delete submission: ' + err?.message);
    } finally {
      setDeletingSubmission(null);
    }
  };

  const handleOpenSubmissionFile = async (filePath) => {
    if (!filePath) {
      toastError(t('managementDashboard.submissions.pdfNotAvailable'));
      return;
    }
    
    try {
      const urlData = await submissionService?.getSubmissionFileUrl(filePath);
      if (!urlData?.url) {
        throw new Error('Signed URL not available');
      }
      window.open(urlData?.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening submission file:', error);
      toastError(t('managementDashboard.submissions.pdfOpenFailed'));
    }
  };

  const handleAddTrack = async () => {
    if (!newTrack?.name?.trim()) {
      toastError(t('managementDashboard.tracks.trackNameRequired'));
      return;
    }

    try {
      await conferenceService?.addTrack(conferenceId, newTrack);
      setNewTrack({ name: '', description: '' });
      await loadConferenceData();
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeyword?.trim()) {
      toastError(t('managementDashboard.keywords.keywordRequired'));
      return;
    }

    if (keywords?.includes(newKeyword?.trim())) {
      toastError(t('managementDashboard.keywords.keywordExists'));
      return;
    }

    try {
      const updatedKeywords = [...keywords, newKeyword?.trim()];
      await conferenceService?.update(conferenceId, { keywords: updatedKeywords });
      setKeywords(updatedKeywords);
      setNewKeyword('');
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    }
  };

  const handleRemoveKeyword = async (keywordToRemove) => {
    try {
      const updatedKeywords = keywords?.filter(k => k !== keywordToRemove);
      await conferenceService?.update(conferenceId, { keywords: updatedKeywords });
      setKeywords(updatedKeywords);
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    }
  };

  // New handlers for paper management
  const handleSelectPaper = (paperId) => {
    setSelectedPapers(prev => {
      if (prev?.includes(paperId)) {
        return prev?.filter(id => id !== paperId);
      } else {
        return [...prev, paperId];
      }
    });
  };

  const handleSelectAllPapers = () => {
    const filteredPapers = getFilteredSubmissions();
    if (selectedPapers?.length === filteredPapers?.length) {
      setSelectedPapers([]);
    } else {
      setSelectedPapers(filteredPapers?.map(p => p?.id));
    }
  };

  const handleOpenReviewerAssignment = async () => {
    if (selectedPapers?.length === 0) {
      toastError(t('managementDashboard.submissions.selectPapersFirst'));
      return;
    }

    try {
      setLoading(true);
      const conferenceReviewers = await assignmentService?.getReviewersForConference(conferenceId);
      setReviewers(conferenceReviewers || []);
      setShowReviewerModal(true);
    } catch (err) {
      toastError(t('managementDashboard.reviewerModal.loadError', { error: err?.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignReviewer = async () => {
    if (!selectedReviewer) {
      toastError(t('managementDashboard.submissions.selectReviewer'));
      return;
    }

    try {
      setAssigningReviewer(true);
      
      // Assign reviewer to all selected papers
      for (const paperId of selectedPapers) {
        await assignmentService?.assignReviewer(conferenceId, paperId, selectedReviewer?.id);
      }

      // Send in-app notification to reviewer
      const reviewer = reviewers?.find(r => r?.id === selectedReviewer?.id);
      if (reviewer) {
        try {
          await notificationService?.createNotification({
            conferenceId,
            notificationType: 'assignment',
            subject: `New Paper Assignment - ${conference?.name}`,
            message: `You have been assigned ${selectedPapers?.length} paper(s) to review for ${conference?.name}. Please visit your Reviewer Dashboard to view your assignments and submit your reviews.`,
            recipientType: 'specific_users',
            recipientFilter: { userIds: [reviewer?.id] },
            sendImmediately: true
          });
        } catch (notifError) {
          console.error('Failed to create in-app notification:', notifError);
          // Don't fail the assignment if notification fails
        }
      }

      toastSuccess(t('managementDashboard.submissions.assignSuccess', { count: selectedPapers?.length, name: `${reviewer?.firstName} ${reviewer?.lastName}` }));
      setShowReviewerModal(false);
      setSelectedPapers([]);
      setSelectedReviewer(null);
      setReviewerSearch('');
      await loadConferenceData();
    } catch (err) {
      toastError(t('managementDashboard.submissions.assignError', { error: err?.message }));
    } finally {
      setAssigningReviewer(false);
    }
  };

  /**
   * Commit a decision the chair has already confirmed in the dialog.
   *
   * The three acceptances all write status 'accepted'; what separates them is
   * `severity`. A paper accepted with revisions is accepted — the author goes
   * to camera-ready, not back into review — so treating minor/major as a
   * different status would misrepresent where the paper stands.
   */
  const commitDecision = async ({ outcome, severity, note, email }) => {
    const paper = pendingDecision?.paper;
    if (!paper) return;

    try {
      setProcessingDecision(paper?.id);

      const newStatus = (outcome?.key === 'reject' || outcome?.key === 'withdraw') ? 'rejected' : 'accepted';
      await decisionService?.updateDecision(paper?.id, newStatus, { severity, note, email });

      // In-app notification, alongside the email.
      await notificationService?.createNotification({
        conferenceId,
        notificationType: 'decision',
        subject: email?.subject || `Paper Decision - ${conference?.name}`,
        message: email?.message || `Your paper "${paper?.title}" has been updated for ${conference?.name}.`,
        recipientType: 'paper_authors',
        recipientFilter: { submissionId: paper?.id },
        sendImmediately: true
      });

      toastSuccess(
        t('managementDashboard.decisions.recorded', {
          outcome: t(`decisionDialog.outcomes.${outcome?.key}`, outcome?.key),
          defaultValue: 'Recorded: {{outcome}}. The author has been emailed.',
        })
      );
      setPendingDecision(null);
      await loadConferenceData();
    } catch (err) {
      toastError(t('managementDashboard.decisions.error', { error: err?.message }));
    } finally {
      setProcessingDecision(null);
    }
  };

  /**
   * Apply one outcome to every selected paper.
   *
   * Confirmed through the same dialog as a single decision, so the chair sees
   * what will be sent before it goes to a dozen authors at once — this used to
   * be a `window.confirm` that named a count and nothing else.
   *
   * A revision note here is shared across the selection, which the dialog says
   * plainly: anything paper-specific belongs in an individual decision. The
   * one exception is 'withdraw': it has no note field of its own (see
   * DecisionDialog), so each paper's email pulls ITS OWN previously-recorded
   * revision note rather than the shared one, since that's the whole point of
   * a withdrawal message — different papers were asked for different changes.
   *
   * If the chair hand-edits the text in the dialog, that edit was rendered
   * against the sample (first) paper — sending it as-is to everyone, as
   * before, is correct there since it's now the chair's deliberate words, not
   * a template. Left untouched, re-render it per paper instead of reusing the
   * sample's title/name/note for the whole batch.
   */
  const commitBulkDecision = async ({ outcome, severity, note, email, edited }) => {
    const papers = bulkPapersFor(outcome);
    if (papers.length === 0) return;

    try {
      setProcessingBulkDecision(true);
      const newStatus = (outcome?.key === 'reject' || outcome?.key === 'withdraw') ? 'rejected' : 'accepted';
      const customTemplate = eventTemplates?.[outcome?.emailEvent] || null;

      for (const paper of papers) {
        const authorName = [paper?.author?.firstName, paper?.author?.lastName].filter(Boolean).join(' ').trim()
          || paper?.author?.email || '';

        const rendered = edited ? email : (renderEmailEvent(outcome?.emailEvent, customTemplate, {
          authorName,
          paperTitle: paper?.title || '',
          conferenceName: conference?.name || '',
          revisionNote: outcome?.key === 'withdraw'
            ? (paper?.revisionNote || '(no specific changes listed)')
            : (note || '(no specific changes listed)'),
          cameraReadyDeadline: conference?.cameraReadyDeadline
            ? new Date(conference.cameraReadyDeadline).toLocaleDateString()
            : 'the camera-ready deadline',
        }) || email);

        await decisionService?.updateDecision(paper?.id, newStatus, { severity, note, email: rendered });

        await notificationService?.createNotification({
          conferenceId,
          notificationType: 'decision',
          subject: rendered?.subject || `Paper Decision - ${conference?.name}`,
          message: rendered?.message || `Your paper "${paper?.title}" has been updated for ${conference?.name}.`,
          recipientType: 'paper_authors',
          recipientFilter: { submissionId: paper?.id },
          sendImmediately: true,
        });
      }

      toastSuccess(
        t('managementDashboard.decisions.bulkRecorded', {
          count: papers.length,
          outcome: t(`decisionDialog.outcomes.${outcome?.key}`, outcome?.key),
          defaultValue: '{{outcome}} applied to {{count}} papers. Authors have been emailed.',
        })
      );
      setPendingBulk(null);
      setSelectedPapers([]);
      await loadConferenceData();
    } catch (err) {
      toastError(t('managementDashboard.decisions.bulkError', { error: err?.message }));
    } finally {
      setProcessingBulkDecision(false);
    }
  };

  const handleOpenDropdown = (submissionId, event) => {
    const button = event?.currentTarget;
    const rect = button?.getBoundingClientRect();
    
    // Calculate position for dropdown
    const dropdownWidth = 192; // w-48 = 12rem = 192px
    const dropdownHeight = 250; // approximate height
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Position dropdown to the left of the button
    let left = rect?.right - dropdownWidth;
    let top = rect?.bottom + 8; // 8px gap below button
    
    // If dropdown would go off bottom of screen, position it above the button
    if (top + dropdownHeight > viewportHeight) {
      top = rect?.top - dropdownHeight - 8;
    }
    
    // If dropdown would go off left of screen, position it to the right
    if (left < 0) {
      left = rect?.left;
    }
    
    // If dropdown would go off right of screen, align to right edge
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 8;
    }
    
    setDropdownPosition({ top, left });
    setOpenDropdown(openDropdown === submissionId ? null : submissionId);
  };

  const getFilteredReviewers = () => {
    if (!reviewerSearch?.trim()) return reviewers;
    
    const searchLower = reviewerSearch?.toLowerCase();
    return reviewers?.filter(r => 
      r?.firstName?.toLowerCase()?.includes(searchLower) ||
      r?.lastName?.toLowerCase()?.includes(searchLower) ||
      r?.email?.toLowerCase()?.includes(searchLower) ||
      r?.institution?.toLowerCase()?.includes(searchLower) ||
      r?.subjectAreas?.some(area => area?.toLowerCase()?.includes(searchLower))
    );
  };

  const getRoleLabel = (role) => {
    if (role === 'chair') return 'Chair';
    if (role === 'organizer') return 'Organizer';
    if (role === 'reviewer') return 'Reviewer';
    return role;
  };

  const buildReviewerInvitationMessage = (email, hasAccount = false) => {
    const registrationUrl = `${window.location.origin}/user-registration`;
    const signInUrl = `${window.location.origin}/user-sign-in`;
    const dashboardUrl = `${window.location.origin}/user-dashboard`;

    if (hasAccount) {
      return `Hello,\n\nYou have been invited to review papers for "${conference?.name}" on Lynk.\n\nTo respond to this invitation:\n1. Sign in to your account:\n${signInUrl}\n\n2. Open your dashboard and accept the reviewer invitation:\n${dashboardUrl}\n\nYou will only be added to this conference's reviewer pool after you accept the invitation.\n\nBest regards,\nArcheditech Team`;
    }

    return `Hello,\n\nYou have been invited to review papers for "${conference?.name}" on Lynk.\n\nTo respond to this invitation:\n1. Register with this email address (${email}) if you do not already have an account:\n${registrationUrl}\n\n2. Sign in to your account:\n${signInUrl}\n\n3. Open your dashboard and accept the reviewer invitation:\n${dashboardUrl}\n\nYou will only be added to this conference's reviewer pool after you accept the invitation.\n\nBest regards,\nArcheditech Team`;
  };

  const sendInviteNotification = async (profile, role) => {
    if (!profile?.id) return;

    const roleLabel = getRoleLabel(role);
    const subject = role === 'reviewer'
      ? `Reviewer Invitation - ${conference?.name}`
      : `Team Invitation - ${conference?.name}`;
    const message = role === 'reviewer'
      ? `You have been invited to join ${conference?.name} as a reviewer. Open your dashboard to accept the invitation.`
      : `You have been added to ${conference?.name} as ${roleLabel}. Open your dashboard to access the conference.`;

    await notificationService?.createNotification({
      conferenceId,
      notificationType: 'custom',
      subject,
      message,
      recipientType: 'specific_users',
      recipientFilter: { userIds: [profile?.id] },
      sendImmediately: true
    });
  };

  const parseInviteEmails = (value) => (
    [...new Set(
      (value || '')
        ?.split(/[\n,;]+/)
        ?.map(part => {
          const match = part?.match(/<([^>]+)>/);
          return (match ? match[1] : part)?.trim()?.toLowerCase();
        })
        ?.filter(Boolean)
    )]
  );

  const handleInviteByEmail = async () => {
    const emails = parseInviteEmails(inviteEmail);
    if (emails?.length === 0) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const allMembers = [
      ...(teamMembers?.chairs || []),
      ...(teamMembers?.organizers || []),
      ...(teamMembers?.reviewers || [])
    ];
    const memberEmails = new Set(allMembers?.map(member => member?.email?.toLowerCase())?.filter(Boolean));
    const pendingRoleEmails = new Set(
      pendingInvitations
        ?.filter(invitation => invitation?.role === newMemberRole)
        ?.map(invitation => invitation?.email?.toLowerCase())
        ?.filter(Boolean)
    );

    try {
      setAddingMember(true);
      const roleName = getRoleLabel(newMemberRole);
      const results = {
        invited: [],
        invalid: [],
        skipped: [],
        emailFailed: []
      };

      for (const email of emails) {
        if (!emailRegex?.test(email)) {
          results.invalid.push(email);
          continue;
        }

        if (memberEmails?.has(email)) {
          results.skipped.push(`${email} (already on the team)`);
          continue;
        }

        if (pendingRoleEmails?.has(email)) {
          results.skipped.push(`${email} (already invited)`);
          continue;
        }

        try {
          const result = await conferenceService?.inviteTeamMember(conferenceId, email, newMemberRole, conference?.name);

          if (newMemberRole === 'reviewer') {
            if (result?.profile?.id) {
              try {
                await sendInviteNotification({
                  id: result?.profile?.id,
                  email,
                  firstName: result?.profile?.first_name,
                  lastName: result?.profile?.last_name
                }, 'reviewer');
              } catch (notificationErr) {
                console.error('Failed to create reviewer invitation notification:', notificationErr);
              }
            }

            try {
              await notificationService?.sendEmail(
                email,
                `Reviewer Invitation - ${conference?.name}`,
                buildReviewerInvitationMessage(email, !!result?.profile),
                'reviewer_invitation'
              );
            } catch (emailErr) {
              console.error('Failed to send reviewer invitation email:', emailErr);
              results.emailFailed.push(email);
            }
          } else if (result?.added && result?.profile) {
            try {
              await sendInviteNotification({
                id: result?.profile?.id,
                email,
                firstName: result?.profile?.first_name,
                lastName: result?.profile?.last_name
              }, newMemberRole);
            } catch (notificationErr) {
              console.error('Failed to create team invitation notification:', notificationErr);
            }

            try {
              await notificationService?.sendEmail(
                email,
                `Team Invitation - ${conference?.name}`,
                `Dear ${result?.profile?.first_name || 'Colleague'},\n\nYou have been added as ${roleName} for the conference "${conference?.name}" on Lynk.\n\nYou can now manage this conference from your dashboard.\n\nVisit: ${window.location.origin}/conference-management-dashboard?conferenceId=${conferenceId}\n\nBest regards,\nArcheditech Team`,
                'team_invitation'
              );
            } catch (emailErr) {
              console.error('Failed to send invitation email:', emailErr);
              results.emailFailed.push(email);
            }
          } else {
            try {
              await notificationService?.sendEmail(
                email,
                `You're Invited to Join "${conference?.name}" - Lynk`,
                `Hello,\n\nYou have been invited to join the organizing team of "${conference?.name}" as ${roleName} on Lynk, an academic conference management platform.\n\nTo accept this invitation, please create an account using this email address (${email}):\n${window.location.origin}/user-registration\n\nOnce registered, you will automatically be assigned the ${roleName} role for this conference.\n\nBest regards,\nArcheditech Team`,
                'team_invitation'
              );
            } catch (emailErr) {
              console.error('Failed to send invitation email:', emailErr);
              results.emailFailed.push(email);
            }
          }

          results.invited.push(email);

          if (newMemberRole === 'reviewer') {
            pendingRoleEmails?.add(email);
          } else if (result?.added) {
            memberEmails?.add(email);
          } else {
            pendingRoleEmails?.add(email);
          }
        } catch (inviteErr) {
          if (inviteErr?.message?.includes('duplicate') || inviteErr?.code === '23505') {
            results.skipped.push(`${email} (already invited or already has this role)`);
          } else {
            results.skipped.push(`${email} (${inviteErr?.message || 'invite failed'})`);
          }
        }
      }

      setInviteEmail('');
      await loadConferenceData();

      const summary = [];
      if (results.invited?.length > 0) {
        summary.push(`${results.invited?.length} invite${results.invited?.length === 1 ? '' : 's'} processed`);
      }
      if (results.invalid?.length > 0) {
        summary.push(`${results.invalid?.length} invalid`);
      }
      if (results.skipped?.length > 0) {
        summary.push(`${results.skipped?.length} skipped`);
      }
      if (results.emailFailed?.length > 0) {
        summary.push(`${results.emailFailed?.length} email${results.emailFailed?.length === 1 ? '' : 's'} failed`);
      }

      const details = [];
      if (results.invalid?.length > 0) {
        details.push(`Invalid: ${results.invalid?.join(', ')}`);
      }
      if (results.skipped?.length > 0) {
        details.push(`Skipped: ${results.skipped?.join(', ')}`);
      }

      toastSuccess([
        summary?.length > 0 ? summary.join(' • ') : 'No invites were processed.',
        ...details
      ]?.join('\n'));
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    } finally {
      setAddingMember(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Cancel this invitation?')) return;
    try {
      await conferenceService?.cancelInvitation(invitationId);
      await loadConferenceData();
    } catch (err) {
      toastError(`Error canceling invitation: ${err?.message}`);
    }
  };

  const handleRemoveTeamMember = async (userId, role) => {
    // Safeguards against lockout.
    if (userId === conference?.createdBy) {
      toastError('This member is the conference owner. Transfer ownership before removing them.');
      return;
    }
    if (role === 'chair' && (teamMembers?.chairs?.length || 0) <= 1) {
      toastError('A conference must have at least one chair. Add another chair before removing this one.');
      return;
    }

    if (!window.confirm(t('managementDashboard.team.removeConfirm'))) return;

    try {
      await conferenceService?.removeConferenceRole(conferenceId, userId, role);
      await loadConferenceData();
      toastSuccess(t('managementDashboard.team.removeSuccess'));
    } catch (err) {
      toastError(t('managementDashboard.team.removeError', { error: err?.message }));
    }
  };

  const handleTransferOwnership = async (userId, name) => {
    if (!window.confirm(
      `Transfer ownership of this conference to ${name}?\n\nThey will become the owner. You will be REMOVED from the team and lose access to this conference. This cannot be undone.`
    )) return;

    try {
      await conferenceService?.transferOwnership(conferenceId, userId);
      toastSuccess(`Ownership transferred to ${name}. You have been removed from the team.`);
      navigate('/user-dashboard');
    } catch (err) {
      toastError(`Failed to transfer ownership: ${err?.message || 'Unknown error'}`);
    }
  };

  /**
   * Accessors for sortable columns whose value isn't a plain top-level field.
   * Reviewer count sorts numerically (so "needs reviewers" floats to one end),
   * author sorts by surname since that's how academics are listed.
   */
  const SUBMISSION_SORT_KEYS = {
    author: (s) => `${s?.author?.lastName || ''} ${s?.author?.firstName || ''}`.trim(),
    track: (s) => s?.trackName || '',
    reviewers: (s) => s?.reviewers?.length || 0,
    submittedAt: (s) => s?.submittedAt || '',
  };

  /* The population the column menus count against: the tab's rows, before any
     column filter. The tab strip and the column filters are different tools —
     the tab picks the working set, the columns narrow within it — so counts
     must not be computed against the already-narrowed list. */
  const tabSubmissions = React.useMemo(() => {
    if (submissionFilter === 'camera_ready') {
      return submissions?.filter((sub) => sub?.status === 'accepted' && sub?.cameraReadySubmittedAt) || [];
    }
    if (submissionFilter !== 'all') {
      return submissions?.filter((sub) => sub?.status === submissionFilter) || [];
    }
    return submissions || [];
  }, [submissions, submissionFilter]);

  const subFilters = useTableFilters(tabSubmissions, React.useMemo(() => ({
    author: {
      label: t('managementDashboard.submissions.columns.author', 'Author'),
      accessor: (s) => s?.authorName
        || [s?.author?.firstName, s?.author?.lastName].filter(Boolean).join(' ').trim()
        || s?.author?.email || '',
      emptyLabel: t('managementDashboard.submissions.noAuthor', 'No author'),
    },
    track: {
      label: t('managementDashboard.submissions.columns.track', 'Track'),
      accessor: (s) => s?.trackName || '',
      emptyLabel: t('managementDashboard.submissions.noTrack', 'No track'),
    },
    status: {
      label: t('managementDashboard.submissions.columns.status', 'Status'),
      accessor: (s) => s?.status || '',
      // Lifecycle order, not alphabetical or by count — a status list that
      // reorders itself as rows change is unreadable.
      order: ['draft', 'submitted', 'under_review', 'revision_required', 'accepted', 'rejected', 'withdrawn'],
      format: (v) => submissionStatusLabel(v, t),
    },
    reviewers: {
      label: t('managementDashboard.submissions.columns.reviewers', 'Reviewers'),
      accessor: (s) => String(s?.reviewers?.length || 0),
      order: ['0', '1', '2', '3', '4', '5'],
      format: (v) => (v === '0'
        ? t('managementDashboard.submissions.unassigned', 'Not assigned')
        : t('managementDashboard.submissions.nReviewers', { count: Number(v), defaultValue: `${v} assigned` })),
    },
  }), [t]));

  const getFilteredSubmissions = () => sortSubmissions(
    subFilters.filterRows(tabSubmissions),
    SUBMISSION_SORT_KEYS
  );

  const getStatusBadgeColor = (status) => {
    const colors = {
      'draft': 'bg-muted text-foreground',
      'submitted': 'bg-primary-subtle text-primary',
      'under_review': 'bg-warning-subtle text-warning',
      'accepted': 'bg-success-subtle text-success',
      'rejected': 'bg-error-subtle text-error',
      'revision_required': 'bg-accent-subtle text-accent'
    };
    return colors?.[status] || 'bg-muted text-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('managementDashboard.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    // A missing ?id= isn't a failure — the user just arrived without picking a
    // conference (bookmark, shared link, manual URL). Explain and route them on
    // rather than showing a red error for something they did nothing wrong in.
    const noId = error === 'missing-conference-id';
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="flex items-center justify-center px-4 py-24">
          <div className="max-w-md text-center">
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center mx-auto mb-4">
              <Icon
                name={noId ? 'FolderOpen' : 'AlertCircle'}
                size={22}
                className={noId ? 'text-muted-foreground' : 'text-error'}
              />
            </div>
            <h1 className="text-lg font-semibold text-foreground mb-1">
              {noId
                ? t('managementDashboard.noConferenceSelected.title', 'No conference selected')
                : t('managementDashboard.loadFailed', 'This conference could not be loaded')}
            </h1>
            <p className="text-sm text-muted-foreground mb-5">
              {noId
                ? t(
                    'managementDashboard.noConferenceSelected.body',
                    'This page manages a single conference. Choose one from your dashboard to continue.'
                  )
                : error}
            </p>
            <Button variant="outline" onClick={() => navigate('/user-dashboard')} iconName="ArrowLeft" iconPosition="left">
              {t('managementDashboard.backToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background bg-mesh">
      <UniversalNavbar />
      <main className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="page-shell">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">{conference?.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">{conference?.shortName}</p>
          </div>
          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-muted/40 text-primary border border-primary/30">
            {conference?.status?.replace('_', ' ')?.toUpperCase()}
          </span>
        </div>

        {/* The in-page tab row that used to sit here is gone. It duplicated the
            conference bar above it — two stacked rows of tabs, with
            "Submissions" and "Emails" appearing in both and pointing at
            different screens. The bar is now the single navigation for a
            conference. */}
        <div className="bg-card rounded-xl border border-border mb-6 overflow-hidden shadow-sm">

          <div className="p-4">
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('managementDashboard.settings.conferenceName')}
                  </label>
                  <Input
                    value={editForm?.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e?.target?.value })}
                    placeholder="Conference name"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('managementDashboard.settings.shortName')}
                  </label>
                  <Input
                    value={editForm?.shortName}
                    onChange={(e) => setEditForm({ ...editForm, shortName: e?.target?.value })}
                    placeholder="Short name"
                  />
                </div>

                <div>
                  <Textarea
                    value={editForm?.description || ''}
                    onChange={(e) => setEditForm({ ...editForm, description: e?.target?.value })}
                    placeholder={t('managementDashboard.settings.descriptionPlaceholder', 'Conference description')}
                    rows={4}
                    maxLength={2000}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('managementDashboard.settings.locationType', 'Location type')}
                  </label>
                  <Select
                    value={editForm?.locationType}
                    onChange={(value) => setEditForm({ ...editForm, locationType: value })}
                    placeholder={t('managementDashboard.settings.selectType', 'Select type')}
                    options={[
                      { value: 'in_person', label: t('managementDashboard.settings.locationTypes.inPerson', 'In person') },
                      { value: 'virtual', label: t('managementDashboard.settings.locationTypes.virtual', 'Virtual') },
                      { value: 'hybrid', label: t('managementDashboard.settings.locationTypes.hybrid', 'Hybrid') },
                    ]}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      {t('managementDashboard.settings.venueName', 'Venue name')}
                    </label>
                    <Input
                      value={editForm?.venueName}
                      onChange={(e) => setEditForm({ ...editForm, venueName: e?.target?.value })}
                      placeholder={t('managementDashboard.settings.venuePlaceholder', 'e.g. Hilton Algiers, Online')}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      {t('managementDashboard.settings.location', 'Location')}
                    </label>
                    <Input
                      value={editForm?.location}
                      onChange={(e) => setEditForm({ ...editForm, location: e?.target?.value })}
                      placeholder="e.g. Algiers, Algeria"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      {t('managementDashboard.settings.startDate')}
                    </label>
                    <Input
                      type="date"
                      value={editForm?.startDate}
                      onChange={(e) => setEditForm({ ...editForm, startDate: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      {t('managementDashboard.settings.endDate')}
                    </label>
                    <Input
                      type="date"
                      value={editForm?.endDate}
                      onChange={(e) => setEditForm({ ...editForm, endDate: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      {t('managementDashboard.settings.paperDeadline')}
                    </label>
                    <Input
                      type="date"
                      value={editForm?.paperDeadline}
                      onChange={(e) => setEditForm({ ...editForm, paperDeadline: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      Review Deadline
                    </label>
                    <Input
                      type="date"
                      value={editForm?.reviewDeadline}
                      onChange={(e) => setEditForm({ ...editForm, reviewDeadline: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      Decision Date
                    </label>
                    <Input
                      type="date"
                      value={editForm?.decisionDate}
                      onChange={(e) => setEditForm({ ...editForm, decisionDate: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      Camera-Ready Deadline
                    </label>
                    <Input
                      type="date"
                      value={editForm?.cameraReadyDeadline}
                      onChange={(e) => setEditForm({ ...editForm, cameraReadyDeadline: e?.target?.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      {t('managementDashboard.settings.status')}
                    </label>
                    {/* NOTE: this list offers decision_made but not cancelled, while the
                        admin filters offer cancelled but not decision_made. The two
                        vocabularies disagree; left as-is to avoid changing behaviour. */}
                    <Select
                      value={editForm?.status}
                      onChange={(value) => setEditForm({ ...editForm, status: value })}
                      options={[
                        { value: 'draft', label: t('managementDashboard.settings.statusOptions.draft') },
                        { value: 'published', label: t('managementDashboard.settings.statusOptions.published') },
                        { value: 'submission_open', label: t('managementDashboard.settings.statusOptions.submissionOpen') },
                        { value: 'submission_closed', label: t('managementDashboard.settings.statusOptions.submissionClosed') },
                        { value: 'review_phase', label: t('managementDashboard.settings.statusOptions.reviewPhase') },
                        { value: 'decision_made', label: t('managementDashboard.settings.statusOptions.decisionMade') },
                        { value: 'completed', label: t('managementDashboard.settings.statusOptions.completed') },
                      ]}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('managementDashboard.settings.fileTypes', 'Accepted file types')}
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {t('managementDashboard.settings.fileTypesHint', 'Which formats authors may submit')}
                  </p>
                  <Select
                    value={editForm?.allowedFileTypes}
                    onChange={(value) => setEditForm({ ...editForm, allowedFileTypes: value })}
                    options={[
                      { value: 'pdf', label: 'PDF only' },
                      { value: 'docx', label: 'DOCX only' },
                      { value: 'both', label: 'PDF and DOCX' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('managementDashboard.settings.bannerImage.title')}
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('managementDashboard.settings.bannerImage.description')}
                  </p>

                  {editForm?.bannerImageUrl && !bannerFile && bannerPreviewUrl && (
                    <div className="mb-3 p-4 bg-muted rounded-lg border border-border">
                      <img
                        src={bannerPreviewUrl}
                        alt="Current banner"
                        className="w-full h-40 object-cover rounded-lg mb-3"
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Icon name="Image" size={20} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{t('managementDashboard.settings.bannerImage.bannerImageLabel')}</p>
                            <p className="text-xs text-muted-foreground">{t('managementDashboard.settings.bannerImage.currentlyUploaded')}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteBannerImage}
                          disabled={saving}
                          iconName="Trash2"
                          iconPosition="left"
                        >
                          {t('managementDashboard.settings.bannerImage.delete')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {bannerFile && (
                    <div className="mb-3 p-4 bg-muted rounded-lg border border-border">
                      <img
                        src={bannerObjectUrl}
                        alt="New banner preview"
                        className="w-full h-40 object-cover rounded-lg mb-3"
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                            <Icon name="Image" size={20} className="text-accent" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{bannerFile?.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(bannerFile?.size / 1024 / 1024)?.toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={handleRemoveBannerFile}
                          iconName="X"
                        />
                      </div>
                    </div>
                  )}

                  {!bannerFile && (
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 bg-muted flex items-center justify-center">
                        <Icon name="Image" size={20} className="text-muted-foreground" />
                      </div>
                      <p className="text-sm text-foreground mb-2">
                        {editForm?.bannerImageUrl ? 'Upload new image to replace existing' : 'Upload conference banner image'}
                      </p>
                      <p className="text-xs text-muted-foreground mb-4">JPEG, PNG, or WebP, up to 5MB</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('banner-file-input')?.click()}
                        iconName="Upload"
                        iconPosition="left"
                      >
                        Choose Image
                      </Button>
                      <input
                        id="banner-file-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleBannerFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {t('managementDashboard.settings.callForPapers.title')}
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Upload a PDF document that can be downloaded from the conference landing page
                  </p>

                  {editForm?.callForPapersUrl && !cfpFile && (
                    <div className="mb-3 p-4 bg-muted rounded-lg border border-border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Icon name="FileText" size={20} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{t('managementDashboard.settings.callForPapers.title')}</p>
                            <p className="text-xs text-muted-foreground">{t('managementDashboard.settings.bannerImage.currentlyUploaded')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadCfp}
                            disabled={cfpUploading}
                            iconName="Download"
                            iconPosition="left"
                          >
                            Download
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleDeleteCfpDocument}
                            disabled={saving}
                            iconName="Trash2"
                            iconPosition="left"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {cfpFile && (
                    <div className="mb-3 p-4 bg-muted rounded-lg border border-border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                            <Icon name="FileText" size={20} className="text-accent" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{cfpFile?.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(cfpFile?.size / 1024 / 1024)?.toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={handleRemoveCfpFile}
                          iconName="X"
                        />
                      </div>
                    </div>
                  )}

                  {!cfpFile && (
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 bg-muted flex items-center justify-center">
                        <Icon name="Upload" size={20} className="text-muted-foreground" />
                      </div>
                      <p className="text-sm text-foreground mb-2">
                        {editForm?.callForPapersUrl ? 'Upload new document to replace existing' : 'Upload call for papers document'}
                      </p>
                      <p className="text-xs text-muted-foreground mb-4">PDF only, up to 10MB</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('cfp-file-input')?.click()}
                        iconName="Upload"
                        iconPosition="left"
                      >
                        Choose File
                      </Button>
                      <input
                        id="cfp-file-input"
                        type="file"
                        accept=".pdf"
                        onChange={handleCfpFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    iconName={saving ? 'Loader' : 'Save'}
                    iconPosition="left"
                  >
                    {saving ? 'Saving...' : t('managementDashboard.settings.saveSettings')}
                  </Button>
                </div>

                {/* Tracks Section */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Layers" size={20} className="text-primary" />
                    Conference Tracks ({tracks?.length || 0})
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        value={newTrack?.name}
                        onChange={(e) => setNewTrack({ ...newTrack, name: e?.target?.value })}
                        placeholder="Track name"
                      />
                      <Input
                        value={newTrack?.description}
                        onChange={(e) => setNewTrack({ ...newTrack, description: e?.target?.value })}
                        placeholder="Track description"
                      />
                    </div>
                    <Button
                      onClick={handleAddTrack}
                      iconName="Plus"
                      iconPosition="left"
                      variant="outline"
                    >
                      {t('managementDashboard.tracks.addTrack')}
                    </Button>

                    <div className="space-y-2 mt-4">
                      {tracks?.map((track) => (
                        <div
                          key={track?.id}
                          className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                        >
                          <div>
                            <h4 className="font-medium text-foreground">{track?.name}</h4>
                            <p className="text-sm text-muted-foreground">{track?.description}</p>
                          </div>
                        </div>
                      ))}
                      {tracks?.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No tracks added yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Keywords Section */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Tag" size={20} className="text-primary" />
                    Conference Keywords ({keywords?.length || 0})
                  </h3>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e?.target?.value)}
                        placeholder="Enter keyword"
                        onKeyPress={(e) => {
                          if (e?.key === 'Enter') {
                            handleAddKeyword();
                          }
                        }}
                      />
                      <Button
                        onClick={handleAddKeyword}
                        iconName="Plus"
                        iconPosition="left"
                        variant="outline"
                      >
                        Add
                      </Button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {keywords?.map((keyword, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary text-sm"
                        >
                          {keyword}
                          <button
                            onClick={() => handleRemoveKeyword(keyword)}
                            className="hover:text-error transition-colors"
                          >
                            <Icon name="X" size={14} />
                          </button>
                        </span>
                      ))}
                      {keywords?.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          No keywords added yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <>
                {/* Add Review Schema Section */}
                <div className="bg-card rounded-lg border border-border p-4 md:p-6 mt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">Review Evaluation Schema</h3>
                      <p className="text-sm text-muted-foreground">
                        Define custom review questions for evaluating submissions
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleManageReviewSchema}
                      iconName="Settings"
                      iconPosition="left"
                    >
                      Manage Schema
                    </Button>
                  </div>

                  {reviewStats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="FileText" size={20} className="text-primary" />
                          <span className="text-sm font-medium text-muted-foreground">Total Papers</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{reviewStats?.totalSubmissions || 0}</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="CheckCircle" size={20} className="text-success" />
                          <span className="text-sm font-medium text-muted-foreground">Reviewed</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{reviewStats?.reviewedSubmissions || 0}</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="Clock" size={20} className="text-warning" />
                          <span className="text-sm font-medium text-muted-foreground">Pending</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{reviewStats?.pendingReviews || 0}</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="Users" size={20} className="text-primary" />
                          <span className="text-sm font-medium text-muted-foreground">Total Reviews</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{reviewStats?.totalReviews || 0}</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'team' && (
              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-foreground">{t('managementDashboard.team.title', 'Team members')}</h2>

                {/* Invite by Email Section */}
                <div className="bg-muted/30 rounded-md border border-border p-3">
                  <h3 className="text-xs font-semibold text-foreground mb-1">{t('managementDashboard.team.inviteByEmail', 'Invite by email')}</h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    {newMemberRole === 'reviewer'
                      ? t('managementDashboard.team.inviteReviewersHelp', 'Invite one or more reviewers by email. Separate addresses with commas, semicolons, or new lines. They will need to register or sign in, then explicitly accept before they appear in your reviewer pool.')
                      : t('managementDashboard.team.inviteHelp', 'Enter one or more email addresses separated by commas, semicolons, or new lines. Registered users are added directly when applicable; others receive an invitation email.')}
                  </p>
                  <div className="flex flex-col gap-3">
                    <div>
                      <Textarea
                        label={t('managementDashboard.team.addresses', 'Email addresses')}
                        value={inviteEmail || ''}
                        onChange={(e) => setInviteEmail(e?.target?.value)}
                        placeholder={t('managementDashboard.team.emailPlaceholder', 'Paste email addresses here…')}
                        rows={4}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm resize-y min-h-[104px]"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Select
                        value={newMemberRole}
                        onChange={setNewMemberRole}
                        className="min-w-[10rem]"
                        options={[
                          { value: 'chair', label: t('managementDashboard.team.roleChair', 'Chair') },
                          { value: 'organizer', label: t('managementDashboard.team.roleOrganizer', 'Organizer') },
                          { value: 'reviewer', label: t('managementDashboard.team.roleReviewer', 'Reviewer') },
                        ]}
                      />
                      <Button
                        onClick={handleInviteByEmail}
                        disabled={addingMember || !inviteEmail?.trim()}
                        iconName={addingMember ? 'Loader' : 'Send'}
                        iconPosition="left"
                      >
                        {addingMember
                          ? t('managementDashboard.team.sending', 'Sending…')
                          : newMemberRole === 'reviewer'
                            ? t('managementDashboard.team.inviteReviewers', 'Invite reviewers')
                            : t('managementDashboard.team.sendInvites', 'Send invites')}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Reviewer directory search */}
                <ReviewerSearch
                  conferenceId={conferenceId}
                  existingReviewerIds={(teamMembers?.reviewers || [])?.map(r => r?.id)}
                  onAdded={loadConferenceData}
                />

                {/* Current Team */}
                {[
                  { key: 'chairs', label: t('managementDashboard.team.chairs'), icon: 'Crown' },
                  { key: 'organizers', label: t('managementDashboard.team.organizers'), icon: 'Users' },
                  { key: 'reviewers', label: t('managementDashboard.team.reviewers', 'Reviewers'), icon: 'ClipboardCheck' }
                ]?.map(({ key, label, icon }) => (
                  <div key={key}>
                    <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name={icon} size={18} className="text-primary" />
                      {label} ({teamMembers?.[key]?.length || 0})
                    </h3>
                    {teamMembers?.[key]?.length > 0 ? (
                      <div className="space-y-2">
                        {teamMembers?.[key]?.map((member) => (
                          <div
                            key={member?.id}
                            className="flex items-center justify-between p-4 bg-card border border-border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 overflow-hidden border-2 border-primary/20 bg-muted flex items-center justify-center flex-shrink-0">
                                {member?.avatarSignedUrl ? (
                                  <img src={member?.avatarSignedUrl} alt={member?.name} className="w-full h-full object-cover" />
                                ) : (
                                  <Icon name="User" size={20} className="text-muted-foreground" />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-foreground">{member?.name}</p>
                                <p className="text-xs text-muted-foreground">{member?.email}</p>
                                {member?.institution && (
                                  <p className="text-xs text-muted-foreground">{member?.institution}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {member?.id === conference?.createdBy && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-warning-subtle text-warning">
                                  <Icon name="Crown" size={12} /> {t('managementDashboard.team.owner', 'Owner')}
                                </span>
                              )}
                              <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary capitalize">
                                {member?.role}
                              </span>
                              {user?.id === conference?.createdBy && (key === 'chairs' || key === 'organizers') && member?.id !== conference?.createdBy && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTransferOwnership(member?.id, member?.name)}
                                  iconName="Crown"
                                  iconPosition="left"
                                  title="Make this member the conference owner"
                                >
                                  {t('managementDashboard.team.makeOwner', 'Make owner')}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveTeamMember(member?.id, member?.role)}
                                iconName="Trash2"
                                className="text-muted-foreground hover:text-error"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        {t('managementDashboard.team.noneYet', { role: label?.toLowerCase(), defaultValue: 'No {{role}} added yet' })}
                      </p>
                    )}
                  </div>
                ))}

                {/* Pending Invitations */}
                {pendingInvitations?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Mail" size={18} className="text-warning" />
                      {t('managementDashboard.team.pendingInvitations', 'Pending invitations')} ({pendingInvitations?.length})
                    </h3>
                    <div className="space-y-2">
                      {pendingInvitations?.map((invitation) => (
                        <div
                          key={invitation?.id}
                          className="flex items-center justify-between p-4 bg-card border border-border rounded-lg border-l-4 border-l-warning"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-warning/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="Mail" size={20} className="text-warning" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{invitation?.email}</p>
                              <p className="text-xs text-muted-foreground">
                                Invited as <span className="capitalize font-medium">{invitation?.role}</span> {invitation?.role === 'reviewer'
                                  ? invitation?.hasAccount ? '— Awaiting acceptance' : '— Awaiting registration and acceptance'
                                  : '— Awaiting registration'}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCancelInvitation(invitation?.id)}
                            iconName="X"
                            className="text-muted-foreground hover:text-error"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'program' && (
              <ProgramTab
                conferenceId={conferenceId}
                conference={conference}
                tracks={tracks}
                submissions={submissions}
              />
            )}

            {activeTab === 'emails' && (
              <EmailTemplatesTab conferenceId={conferenceId} />
            )}

            {activeTab === 'announcements' && (
              <AnnouncementsTab conferenceId={conferenceId} />
            )}

            {activeTab === 'badges' && (
              <BadgesTab conferenceId={conferenceId} />
            )}

            {activeTab === 'submissions' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-foreground">{t('managementDashboard.submissions.heading', 'Submissions')}</h2>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="default"
                      onClick={handleOpenReviewerAssignment}
                      iconName="UserPlus"
                      iconPosition="left"
                      disabled={selectedPapers?.length === 0}
                    >
                      {t('managementDashboard.submissions.assignReviewer')} ({selectedPapers?.length || 0})
                    </Button>
                    <div className="relative">
                      <Button
                        variant="outline"
                        onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                        iconName="Download"
                        iconPosition="left"
                        disabled={selectedPapers?.length === 0 || downloadingFiles}
                      >
                        {downloadingFiles ? 'Preparing ZIP...' : `Download Files (${selectedPapers?.length || 0})`}
                      </Button>
                      {showDownloadDropdown && selectedPapers?.length > 0 && (() => {
                        const selectedSubs = submissions?.filter(s => selectedPapers.includes(s?.id)) || [];
                        const hasCameraReady = selectedSubs.some(s => s?.cameraReadyUrl);
                        const hasPermissionForm = selectedSubs.some(s => s?.permissionFormUrl);
                        const runDownload = (kind) => {
                          setShowDownloadDropdown(false);
                          handleDownloadSelectedFiles(kind);
                        };
                        return (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setShowDownloadDropdown(false)}
                            />
                            <div className="absolute end-0 mt-2 w-56 bg-card border border-border rounded-md shadow-lg z-50">
                              <div className="py-1">
                                <button
                                  onClick={() => runDownload('camera_ready')}
                                  disabled={!hasCameraReady}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-45 disabled:pointer-events-none"
                                >
                                  <Icon name="FileText" size={16} />
                                  <span>Camera-Ready only</span>
                                </button>
                                <button
                                  onClick={() => runDownload('permission_form')}
                                  disabled={!hasPermissionForm}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-45 disabled:pointer-events-none"
                                >
                                  <Icon name="FileCheck" size={16} />
                                  <span>Permission Form only</span>
                                </button>
                                <div className="my-1 border-t border-border" />
                                <button
                                  onClick={() => runDownload('package')}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                                >
                                  <Icon name="Package" size={16} />
                                  <span>Files Package (all)</span>
                                </button>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="relative">
                      <Button
                        variant="outline"
                        onClick={() => setShowBulkActionsDropdown(!showBulkActionsDropdown)}
                        iconName="MoreVertical"
                        iconPosition="left"
                        disabled={selectedPapers?.length === 0 || processingBulkDecision}
                      >
                        {processingBulkDecision ? 'Processing...' : `Actions (${selectedPapers?.length || 0})`}
                      </Button>
                      {showBulkActionsDropdown && selectedPapers?.length > 0 && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowBulkActionsDropdown(false)}
                          />
                          <div className="absolute end-0 mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-50">
                            <div className="py-1">
                              {DECISION_OUTCOMES.map((o) => (
                                <button
                                  key={o.key}
                                  onClick={() => openBulk(o)}
                                  disabled={processingBulkDecision}
                                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors disabled:opacity-50 ${
                                    o.key === 'reject' || o.key === 'withdraw'
                                      ? 'text-error hover:bg-error-subtle'
                                      : o.key === 'accept'
                                        ? 'text-success hover:bg-success-subtle'
                                        : 'text-foreground hover:bg-muted'
                                  }`}
                                >
                                  <Icon name={o.icon} size={16} />
                                  <span>{t(`decisionDialog.outcomes.${o.key}`, o.key)}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleExportToExcel}
                      iconName="Download"
                      iconPosition="left"
                    >
                      {t('managementDashboard.submissions.exportToExcel')}
                    </Button>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex flex-wrap gap-2 border-b border-border">
                  <button
                    onClick={() => setSubmissionFilter('all')}
                    className={`px-2.5 py-1.5 text-xs font-medium border-b-2 transition-250 ${
                      submissionFilter === 'all' ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('managementDashboard.submissions.filterAll')} ({submissions?.length || 0})
                  </button>
                  <button
                    onClick={() => setSubmissionFilter('submitted')}
                    className={`px-2.5 py-1.5 text-xs font-medium border-b-2 transition-250 ${
                      submissionFilter === 'submitted' ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('managementDashboard.submissions.filters.submitted', 'Submitted')} ({submissions?.filter(s => s?.status === 'submitted')?.length || 0})
                  </button>
                  <button
                    onClick={() => setSubmissionFilter('under_review')}
                    className={`px-2.5 py-1.5 text-xs font-medium border-b-2 transition-250 ${
                      submissionFilter === 'under_review' ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('managementDashboard.submissions.filters.under_review', 'Under Review')} ({submissions?.filter(s => s?.status === 'under_review')?.length || 0})
                  </button>
                  <button
                    onClick={() => setSubmissionFilter('accepted')}
                    className={`px-2.5 py-1.5 text-xs font-medium border-b-2 transition-250 ${
                      submissionFilter === 'accepted' ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('managementDashboard.submissions.filters.accepted', 'Accepted')} ({submissions?.filter(s => s?.status === 'accepted')?.length || 0})
                  </button>
                  <button
                    onClick={() => setSubmissionFilter('rejected')}
                    className={`px-2.5 py-1.5 text-xs font-medium border-b-2 transition-250 ${
                      submissionFilter === 'rejected' ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('managementDashboard.submissions.filters.rejected', 'Rejected')} ({submissions?.filter(s => s?.status === 'rejected')?.length || 0})
                  </button>
                  <button
                    onClick={() => setSubmissionFilter('revision_required')}
                    className={`px-2.5 py-1.5 text-xs font-medium border-b-2 transition-250 ${
                      submissionFilter === 'revision_required' ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('managementDashboard.submissions.filters.revision_required', 'Revision Required')} ({submissions?.filter(s => s?.status === 'revision_required')?.length || 0})
                  </button>
                  <button
                    onClick={() => setSubmissionFilter('camera_ready')}
                    className={`px-2.5 py-1.5 text-xs font-medium border-b-2 transition-250 ${
                      submissionFilter === 'camera_ready' ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('managementDashboard.submissions.filterCameraReady')} ({submissions?.filter(s => s?.status === 'accepted' && s?.cameraReadySubmittedAt)?.length || 0}/{submissions?.filter(s => s?.status === 'accepted')?.length || 0})
                  </button>
                </div>

                {/* Which columns are filtered, stated above the rows. The
                    header icons say a filter exists; this says which columns
                    and what they are set to, and lets you drop one without
                    hunting for the header that owns it. */}
                <ActiveFilters
                  className="mb-3"
                  filters={subFilters.chips}
                  resultCount={getFilteredSubmissions()?.length || 0}
                  totalCount={tabSubmissions?.length || 0}
                  onClearAll={subFilters.clearAll}
                />

                {/* Camera-Ready summary card */}
                {submissionFilter === 'camera_ready' && (
                  <div className="mb-4 p-3 rounded-lg border border-primary/30 bg-primary-subtle/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary-subtle flex items-center justify-center">
                          <Icon name="FileCheck" size={16} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">Camera-Ready Submissions</p>
                          <p className="text-xs text-muted-foreground">
                            {submissions?.filter(s => s?.status === 'accepted' && s?.cameraReadySubmittedAt)?.length} of {submissions?.filter(s => s?.status === 'accepted')?.length} accepted papers received
                          </p>
                        </div>
                      </div>
                      {conference?.cameraReadyDeadline && (
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Deadline</p>
                          <p className={`text-sm font-semibold ${new Date(conference?.cameraReadyDeadline) < new Date() ? 'text-error' : 'text-foreground'}`}>
                            {formatDate(conference?.cameraReadyDeadline)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {getFilteredSubmissions()?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="FileText" size={24} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">
                      {submissionFilter === 'all' ? 'No submissions yet'
                        : submissionFilter === 'camera_ready' ? 'No camera-ready files uploaded yet'
                        : `No ${submissionFilter?.replace('_', ' ')} submissions`}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table-clear">
                      <thead>
                        <tr>
                          <th className="!px-4 !w-10">
                            <input
                              type="checkbox"
                              checked={selectedPapers?.length === getFilteredSubmissions()?.length && getFilteredSubmissions()?.length > 0}
                              onChange={handleSelectAllPapers}
                              className="w-4 h-4 text-primary border-border focus:ring-primary"
                            />
                          </th>
                          <SortableTh field="title" sort={subSort} onSort={toggleSubSort}>{t('managementDashboard.submissions.columns.title', 'Title')}</SortableTh>
                          <SortableTh field="author" sort={subSort} onSort={toggleSubSort} filter={subFilters.headerProps('author')}>{t('managementDashboard.submissions.columns.author', 'Author')}</SortableTh>
                          <SortableTh field="track" sort={subSort} onSort={toggleSubSort} filter={subFilters.headerProps('track')}>{t('managementDashboard.submissions.columns.track', 'Track')}</SortableTh>
                          <SortableTh field="reviewers" sort={subSort} onSort={toggleSubSort} align="end" filter={subFilters.headerProps('reviewers')}>{t('managementDashboard.submissions.columns.reviewers', 'Reviewers')}</SortableTh>
                          <th>{t('managementDashboard.submissions.columns.reviewStatus', 'Review status')}</th>
                          <SortableTh field="status" sort={subSort} onSort={toggleSubSort} filter={subFilters.headerProps('status')}>{t('managementDashboard.submissions.columns.status', 'Status')}</SortableTh>
                          <SortableTh field="submittedAt" sort={subSort} onSort={toggleSubSort} align="end">{t('managementDashboard.submissions.columns.submitted', 'Submitted')}</SortableTh>
                          <th className="col-tight">{t('managementDashboard.submissions.columns.actions', 'Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredSubmissions()?.map((submission) => {
                          const isExpanded = expandedSubmission === submission?.id;
                          const decisionLock = getDecisionLock(submission, conference);
                          const completedReviews = submission?.reviewers?.filter(r => r?.reviewStatus === 'completed') || [];
                          const avgScore = completedReviews?.length > 0
                            ? (completedReviews?.reduce((sum, r) => sum + (r?.overallScore || 0), 0) / completedReviews?.length)?.toFixed(1)
                            : null;
                          // An average hides a split verdict: two reviewers at 3
                          // and 9 average to the same 6 as two reviewers at 6.
                          // This is the paper a chair should actually read.
                          const agreement = reviewAgreement(completedReviews);
                          return (
                          <React.Fragment key={submission?.id}>
                          <tr
                            className={`cursor-pointer ${isExpanded ? 'bg-primary-subtle/40' : ''}`}
                            onClick={() => setExpandedSubmission(isExpanded ? null : submission?.id)}
                          >
                            <td className="!px-4" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedPapers?.includes(submission?.id)}
                                onChange={() => handleSelectPaper(submission?.id)}
                                className="w-4 h-4 text-primary border-border focus:ring-primary"
                              />
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <Icon
                                  name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                                  size={14}
                                  className="text-muted-foreground flex-shrink-0"
                                />
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {submission?.paperNumber && (
                                      <span className="text-xs font-mono text-muted-foreground flex-shrink-0">#{submission.paperNumber}</span>
                                    )}
                                    {submission?.title}
                                  </div>
                                  {submission?.pdfFileName && (
                                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                      <Icon name="FileText" size={12} />
                                      {submission?.pdfFileName}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-muted-foreground">
                              <div className="font-medium text-foreground">{submission?.author?.firstName} {submission?.author?.lastName}</div>
                              <div className="text-[11px] text-muted-foreground">{submission?.author?.institution}</div>
                            </td>
                            <td className="text-muted-foreground">{submission?.trackName || 'N/A'}</td>
                            <td>
                              {submission?.reviewers?.length > 0 ? (
                                <div className="space-y-0.5">
                                  {submission?.reviewers?.map((reviewer, idx) => (
                                    <div key={idx} className="text-foreground">
                                      {reviewer?.firstName} {reviewer?.lastName}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground italic">{t('managementDashboard.submissions.notAssigned', 'Not assigned')}</span>
                              )}
                            </td>
                            <td>
                              {submission?.reviewers?.length > 0 ? (
                                <div className="space-y-0.5">
                                  {submission?.reviewers?.map((reviewer, idx) => {
                                    const statusColor = reviewer?.reviewStatus === 'completed'
                                      ? 'text-success'
                                      : reviewer?.reviewStatus === 'in_progress' ?'text-warning' :'text-muted-foreground';
                                    const statusIcon = reviewer?.reviewStatus === 'completed' ?'CheckCircle'
                                      : reviewer?.reviewStatus === 'in_progress' ?'Clock' :'Circle';
                                    return (
                                      <div key={idx} className={`flex items-center gap-1 ${statusColor}`}>
                                        <Icon name={statusIcon} size={12} />
                                        <span>{reviewer?.reviewStatus === 'completed' ? 'Done' : reviewer?.reviewStatus === 'in_progress' ? 'In Progress' : 'Pending'}</span>
                                      </div>
                                    );
                                  })}
                                  {agreement.disagrees && (
                                    <div
                                      className="flex items-center gap-1 text-accent mt-0.5"
                                      title={t('managementDashboard.submissions.disagreeTip', {
                                        min: agreement.min,
                                        max: agreement.max,
                                        defaultValue: 'Scores range from {{min}} to {{max}} — worth reading properly.',
                                      })}
                                    >
                                      <Icon name="GitCompareArrows" size={12} />
                                      <span>{t('managementDashboard.submissions.disagree', 'Split')}</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground italic">-</span>
                              )}
                            </td>
                            <td>
                              <span className={`inline-block px-2 py-0.5 text-xs font-semibold ${getStatusBadgeColor(submission?.status)}`}>
                                {submissionStatusLabel(submission?.status, t)}
                              </span>
                              {/* Accepted papers owe two documents. Tracked
                                  separately: a combined indicator would hide
                                  which one is actually missing, and chasing the
                                  wrong document wastes everyone's time. */}
                              {submission?.status === 'accepted' && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {submission?.cameraReadySubmittedAt ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-success-subtle text-success border border-success-border">
                                      <Icon name="CheckCircle" size={10} />
                                      {t('managementDashboard.submissions.crReceived', 'Paper in')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-warning-subtle text-warning border border-warning-border">
                                      <Icon name="Clock" size={10} />
                                      {t('managementDashboard.submissions.crPending', 'Paper due')}
                                    </span>
                                  )}
                                  {submission?.permissionFormUrl ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-success-subtle text-success border border-success-border">
                                      <Icon name="CheckCircle" size={10} />
                                      {t('managementDashboard.submissions.formReceived', 'Form in')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-warning-subtle text-warning border border-warning-border">
                                      <Icon name="Clock" size={10} />
                                      {t('managementDashboard.submissions.formPending', 'Form due')}
                                    </span>
                                  )}
                                  {submission?.revisionSeverity && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-accent-subtle text-accent border border-accent-border">
                                      <Icon name="PencilLine" size={10} />
                                      {t(
                                        submission.revisionSeverity === 'major'
                                          ? 'managementDashboard.submissions.majorRev'
                                          : 'managementDashboard.submissions.minorRev',
                                        submission.revisionSeverity === 'major' ? 'Major rev.' : 'Minor rev.'
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="text-muted-foreground">
                              {formatDate(submission?.submittedAt) || 'N/A'}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className="relative flex items-center gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteSubmission(submission?.id); }}
                                  disabled={deletingSubmission === submission?.id}
                                  className="p-2 rounded-md transition-colors text-muted-foreground hover:text-error hover:bg-error-subtle disabled:opacity-50"
                                  title="Delete submission"
                                >
                                  {deletingSubmission === submission?.id
                                    ? <Icon name="Loader" size={16} className="animate-spin" />
                                    : <Icon name="Trash2" size={16} />
                                  }
                                </button>
                                <button
                                  onClick={(e) => handleOpenDropdown(submission?.id, e)}
                                  className="p-2 hover:bg-muted rounded-md transition-colors"
                                >
                                  <Icon name="MoreVertical" size={16} className="text-muted-foreground" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {/* Expanded review details row */}
                          {isExpanded && (
                            <tr className="border-b border-border bg-muted/20">
                              <td colSpan={9} className="p-0">
                                <div className="px-6 py-4 space-y-4">
                                  {/* Summary bar */}
                                  <div className="flex items-center gap-4 flex-wrap">
                                    <div className="flex items-center gap-2 text-sm">
                                      <Icon name="Users" size={14} className="text-muted-foreground" />
                                      <span className="text-muted-foreground">Reviews:</span>
                                      <span className="font-medium text-foreground">
                                        {completedReviews?.length}/{submission?.reviewers?.length || 0} completed
                                      </span>
                                    </div>
                                    {avgScore && (
                                      <div className="flex items-center gap-2 text-sm">
                                        <Icon name="BarChart3" size={14} className="text-muted-foreground" />
                                        <span className="text-muted-foreground">Avg Score:</span>
                                        <span className={`font-bold ${parseFloat(avgScore) >= 7 ? 'text-success' : parseFloat(avgScore) >= 4 ? 'text-warning' : 'text-error'}`}>
                                          {avgScore}/10
                                        </span>
                                      </div>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleViewSubmission(submission); }}
                                      className="text-sm text-primary hover:underline flex items-center gap-1 ml-auto"
                                    >
                                      <Icon name="Eye" size={14} />
                                      View Full Details
                                    </button>
                                  </div>

                                  {/* Individual reviews */}
                                  {completedReviews?.length > 0 ? (
                                    <div className="space-y-3">
                                      {completedReviews?.map((reviewer, idx) => (
                                        <div key={idx} className="bg-card border border-border rounded-lg p-4">
                                          <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                              <div className="w-7 h-7 bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                                {reviewer?.firstName?.[0]}{reviewer?.lastName?.[0]}
                                              </div>
                                              <div>
                                                <div className="text-sm font-medium text-foreground">
                                                  {reviewer?.firstName} {reviewer?.lastName}
                                                </div>
                                                <div className="text-xs text-muted-foreground">{reviewer?.email}</div>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                              {reviewer?.decision && (
                                                <span className={`px-2 py-0.5 text-xs font-medium ${
                                                  reviewer?.decision === 'accept' ? 'bg-success-subtle text-success' :
                                                  reviewer?.decision === 'reject' ? 'bg-error-subtle text-error' :
                                                  reviewer?.decision === 'minor_revision' ? 'bg-primary-subtle text-primary' :
                                                  'bg-warning-subtle text-warning'
                                                }`}>
                                                  {reviewer?.decision?.replace('_', ' ')}
                                                </span>
                                              )}
                                              <div className="text-right">
                                                <div className={`text-lg font-bold ${
                                                  reviewer?.overallScore >= 7 ? 'text-success' :
                                                  reviewer?.overallScore >= 4 ? 'text-warning' : 'text-error'
                                                }`}>
                                                  {reviewer?.overallScore}/10
                                                </div>
                                                {reviewer?.confidenceLevel && (
                                                  <div className="text-xs text-muted-foreground">
                                                    Confidence: {reviewer?.confidenceLevel}/5
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>

                                          {/* Feedback sections */}
                                          <div className="space-y-2 text-sm">
                                            {reviewer?.detailedFeedback && (
                                              <div>
                                                <div className="font-medium text-foreground text-xs mb-1">Feedback</div>
                                                <p className="text-muted-foreground text-xs leading-relaxed whitespace-pre-wrap">
                                                  {reviewer?.detailedFeedback}
                                                </p>
                                              </div>
                                            )}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                              {reviewer?.strengths && (
                                                <div className="bg-success-subtle p-2">
                                                  <div className="font-medium text-success text-xs mb-1 flex items-center gap-1">
                                                    <Icon name="ThumbsUp" size={12} /> Strengths
                                                  </div>
                                                  <p className="text-success text-xs leading-relaxed whitespace-pre-wrap">{reviewer?.strengths}</p>
                                                </div>
                                              )}
                                              {reviewer?.weaknesses && (
                                                <div className="bg-error-subtle p-2">
                                                  <div className="font-medium text-error text-xs mb-1 flex items-center gap-1">
                                                    <Icon name="ThumbsDown" size={12} /> Weaknesses
                                                  </div>
                                                  <p className="text-error text-xs leading-relaxed whitespace-pre-wrap">{reviewer?.weaknesses}</p>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-center py-4 text-sm text-muted-foreground">
                                      <Icon name="Clock" size={20} className="mx-auto mb-2 text-muted-foreground" />
                                      {submission?.reviewers?.length > 0
                                        ? 'No completed reviews yet. Reviews are still in progress.'
                                        : 'No reviewers assigned to this paper yet.'}
                                    </div>
                                  )}

                                  {/* Decision actions. Four outcomes, not two:
                                      a paper can be accepted outright or
                                      accepted on condition of minor or major
                                      changes. Reject sits apart from the three
                                      acceptances so it can't be hit by
                                      momentum. */}
                                  {(submission?.status === 'submitted' || submission?.status === 'under_review') && (
                                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                                      <span className="text-sm text-muted-foreground mr-auto">
                                        {t('managementDashboard.decisions.label', 'Decision:')}
                                      </span>
                                      {decisionLock && (
                                        <span className="w-full flex items-center gap-1.5 text-xs text-muted-foreground order-last">
                                          <Icon name="Lock" size={12} className="flex-shrink-0" />
                                          {decisionLockMessage(decisionLock)}
                                        </span>
                                      )}
                                      {DECISION_OUTCOMES.filter(o => o.key !== 'reject' && o.key !== 'withdraw').map((o) => (
                                        <Button
                                          key={o.key}
                                          variant="outline"
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); openDecision(submission, o); }}
                                          disabled={Boolean(decisionLock) || processingDecision === submission?.id}
                                          iconName={o.icon}
                                          iconPosition="left"
                                          className={o.key === 'accept' ? 'text-success border-success-border hover:bg-success-subtle' : undefined}
                                        >
                                          {t(`decisionDialog.outcomes.${o.key}`, o.key)}
                                        </Button>
                                      ))}
                                      <span className="w-px h-6 bg-border mx-1" aria-hidden="true" />
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); openDecision(submission, DECISION_OUTCOMES.find(o => o.key === 'reject')); }}
                                        disabled={Boolean(decisionLock) || processingDecision === submission?.id}
                                        iconName="XCircle"
                                        iconPosition="left"
                                        className="text-error border-error-border hover:bg-error-subtle"
                                      >
                                        {t('decisionDialog.outcomes.reject', 'Reject')}
                                      </Button>
                                    </div>
                                  )}
                                  {submission?.status === 'accepted' && (
                                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                                      <Icon name="CheckCircle" size={16} className="text-success" />
                                      <span className="text-sm font-medium text-success mr-auto">
                                        {t('managementDashboard.submissions.paperAccepted', 'Paper accepted')}
                                      </span>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); openDecision(submission, DECISION_OUTCOMES.find(o => o.key === 'withdraw')); }}
                                        disabled={processingDecision === submission?.id}
                                        iconName="Undo2"
                                        iconPosition="left"
                                        className="text-error border-error-border hover:bg-error-subtle"
                                      >
                                        {t('decisionDialog.outcomes.withdraw', 'Withdraw acceptance')}
                                      </Button>
                                    </div>
                                  )}
                                  {submission?.status === 'rejected' && (
                                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                                      <Icon name="XCircle" size={16} className="text-error" />
                                      <span className="text-sm font-medium text-error">
                                        {t('managementDashboard.submissions.paperRejected', 'Paper rejected')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Dropdown portal - rendered outside table */}
                {openDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setOpenDropdown(null)}
                    />
                    <div 
                      className="fixed bg-card border border-border rounded-md shadow-lg z-50 w-48"
                      style={{
                        top: `${dropdownPosition?.top}px`,
                        left: `${dropdownPosition?.left}px`
                      }}
                    >
                      <div className="py-1">
                        {(() => {
                          const submission = submissions?.find(s => s?.id === openDropdown);
                          if (!submission) return null;
                          
                          return (
                            <>
                              <button
                                onClick={() => {
                                  handleViewSubmission(submission);
                                  setOpenDropdown(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                              >
                                <Icon name="Eye" size={16} />
                                <span>View Details</span>
                              </button>
                              
                              {submission?.pdfUrl && (
                                <button
                                  onClick={() => {
                                    handleOpenSubmissionFile(submission?.pdfUrl);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                                >
                                  <Icon name="FileText" size={16} />
                                  <span>Open File</span>
                                </button>
                              )}
                              
                              {(submission?.status === 'submitted' || submission?.status === 'under_review') && (
                                <>
                                  <div className="border-t border-border my-1" />
                                  {getDecisionLock(submission, conference) && (
                                    <p className="flex items-start gap-1.5 px-4 py-1.5 text-xs text-muted-foreground">
                                      <Icon name="Lock" size={12} className="flex-shrink-0 mt-0.5" />
                                      {decisionLockMessage(getDecisionLock(submission, conference))}
                                    </p>
                                  )}
                                  {DECISION_OUTCOMES.filter(o => o.key !== 'withdraw').map((o) => (
                                    <button
                                      key={o.key}
                                      onClick={() => { openDecision(submission, o); setOpenDropdown(null); }}
                                      disabled={Boolean(getDecisionLock(submission, conference)) || processingDecision === submission?.id}
                                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors disabled:opacity-50 ${
                                        o.key === 'reject'
                                          ? 'text-error hover:bg-error-subtle'
                                          : o.key === 'accept'
                                            ? 'text-success hover:bg-success-subtle'
                                            : 'text-foreground hover:bg-muted'
                                      }`}
                                    >
                                      <Icon name={o.icon} size={16} />
                                      <span>{t(`decisionDialog.outcomes.${o.key}`, o.key)}</span>
                                    </button>
                                  ))}
                                </>
                              )}
                              {submission?.status === 'accepted' && (
                                <>
                                  <div className="border-t border-border my-1" />
                                  <button
                                    onClick={() => { openDecision(submission, DECISION_OUTCOMES.find(o => o.key === 'withdraw')); setOpenDropdown(null); }}
                                    disabled={processingDecision === submission?.id}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error hover:bg-error-subtle transition-colors disabled:opacity-50"
                                  >
                                    <Icon name="Undo2" size={16} />
                                    <span>{t('decisionDialog.outcomes.withdraw', 'Withdraw acceptance')}</span>
                                  </button>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </main>
      <DecisionDialog
        open={Boolean(pendingBulk)}
        outcome={pendingBulk}
        papers={pendingBulk ? bulkPapersFor(pendingBulk) : []}
        conference={conference}
        customTemplate={eventTemplates?.[pendingBulk?.emailEvent] || null}
        saving={processingBulkDecision}
        onCancel={() => setPendingBulk(null)}
        onConfirm={commitBulkDecision}
      />

      <DecisionDialog
        open={Boolean(pendingDecision)}
        outcome={pendingDecision?.outcome}
        paper={pendingDecision?.paper}
        conference={conference}
        customTemplate={eventTemplates?.[pendingDecision?.outcome?.emailEvent] || null}
        saving={Boolean(processingDecision)}
        onCancel={() => setPendingDecision(null)}
        onConfirm={commitDecision}
      />

      {/* Submission Detail Modal */}
      {showSubmissionModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Submission Details</h2>
              <button
                onClick={() => setShowSubmissionModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title and Status */}
              <div>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-2xl font-bold text-foreground">{selectedSubmission?.title}</h3>
                  <span className={`px-3 py-1 text-sm font-medium ${getStatusBadgeColor(selectedSubmission?.status)}`}>
                    {submissionStatusLabel(selectedSubmission?.status, t)}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Submitted on {formatDate(selectedSubmission?.submittedAt, { month: 'long' }) || 'N/A'}
                </div>
              </div>

              {/* PDF Download Section */}
              {selectedSubmission?.pdfUrl && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <Icon name="FileText" size={24} className="text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{selectedSubmission?.pdfFileName || 'Submission file'}</div>
                        <div className="text-sm text-muted-foreground">
                          {selectedSubmission?.pdfFileSize ? `${(selectedSubmission?.pdfFileSize / 1024 / 1024)?.toFixed(2)} MB` : 'Submission document'}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleOpenSubmissionFile(selectedSubmission?.pdfUrl)}
                      iconName="ExternalLink"
                      iconPosition="right"
                    >
                      Open File
                    </Button>
                  </div>
                </div>
              )}

              {/* Author Information */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="User" size={16} />
                  Author Information
                </h4>
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Name:</span>
                    <span className="text-sm text-muted-foreground">
                      {selectedSubmission?.author?.firstName} {selectedSubmission?.author?.lastName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Email:</span>
                    <span className="text-sm text-muted-foreground">{selectedSubmission?.author?.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Institution:</span>
                    <span className="text-sm text-muted-foreground">{selectedSubmission?.author?.institution}</span>
                  </div>
                </div>
              </div>

              {/* Co-Authors */}
              {selectedSubmission?.coAuthors && selectedSubmission?.coAuthors?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Users" size={16} />
                    Co-Authors ({selectedSubmission?.coAuthors?.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedSubmission?.coAuthors?.map((coAuthor, index) => (
                      <div key={index} className="bg-muted/30 rounded-lg p-3">
                        <div className="font-medium text-foreground text-sm">
                          {coAuthor?.firstName} {coAuthor?.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground">{coAuthor?.email}</div>
                        {coAuthor?.institution && (
                          <div className="text-xs text-muted-foreground">{coAuthor?.institution}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Track */}
              {selectedSubmission?.trackName && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Icon name="Folder" size={16} />
                    Track
                  </h4>
                  <div className="bg-muted/30 rounded-lg px-4 py-2 inline-block">
                    <span className="text-sm text-foreground">{selectedSubmission?.trackName}</span>
                  </div>
                </div>
              )}

              {/* Keywords */}
              {selectedSubmission?.keywords && selectedSubmission?.keywords?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Tag" size={16} />
                    Keywords
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedSubmission?.keywords?.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Abstract */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="FileText" size={16} />
                  Abstract
                </h4>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                    {selectedSubmission?.abstract}
                  </p>
                </div>
              </div>

              {/* Supplementary Files */}
              {selectedSubmission?.supplementaryFiles && selectedSubmission?.supplementaryFiles?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Paperclip" size={16} />
                    Supplementary Files ({selectedSubmission?.supplementaryFiles?.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedSubmission?.supplementaryFiles?.map((file, index) => (
                      <div key={index} className="bg-muted/30 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon name="File" size={16} className="text-muted-foreground" />
                          <span className="text-sm text-foreground">{file?.name || `File ${index + 1}`}</span>
                        </div>
                        {file?.size && (
                          <span className="text-xs text-muted-foreground">
                            {(file?.size / 1024)?.toFixed(2)} KB
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-background border-t border-border px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSubmissionModal(false)}
              >
                Close
              </Button>
              {selectedSubmission?.pdfUrl && (
                <Button
                  onClick={() => handleOpenSubmissionFile(selectedSubmission?.pdfUrl)}
                  iconName="ExternalLink"
                  iconPosition="right"
                >
                  Open File
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Reviewer Assignment Modal */}
      {showReviewerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">{t('managementDashboard.submissions.assignReviewer')}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Assigning to {selectedPapers?.length} selected paper(s)
                </p>
              </div>
              <button
                onClick={() => {
                  setShowReviewerModal(false);
                  setSelectedReviewer(null);
                  setReviewerSearch('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Search Bar */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">
                  Search Accepted Reviewers
                </label>
                <Input
                  value={reviewerSearch}
                  onChange={(e) => setReviewerSearch(e?.target?.value)}
                  placeholder="Search by name, email, institution, or expertise..."
                  iconName="Search"
                />
              </div>

              {/* Reviewer List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {getFilteredReviewers()?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {reviewerSearch ? 'No accepted reviewers found matching your search' : 'No accepted reviewers available yet. Invite them from the Team tab first.'}
                    </p>
                  </div>
                ) : (
                  getFilteredReviewers()?.map((reviewer) => (
                    <div
                      key={reviewer?.id}
                      onClick={() => setSelectedReviewer(reviewer)}
                      className={`border rounded-lg p-4 cursor-pointer transition-250 ${
                        selectedReviewer?.id === reviewer?.id
                          ? 'border-primary bg-primary/5' :'border-transparent hover:border-primary/50 hover:bg-muted/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-foreground">
                              {reviewer?.firstName} {reviewer?.lastName}
                            </h3>
                            {selectedReviewer?.id === reviewer?.id && (
                              <Icon name="CheckCircle" size={18} className="text-primary" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{reviewer?.email}</p>
                          <p className="text-sm text-muted-foreground mb-2">{reviewer?.institution}</p>
                          
                          {reviewer?.subjectAreas?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {reviewer?.subjectAreas?.slice(0, 3)?.map((area, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-muted text-xs"
                                >
                                  {area}
                                </span>
                              ))}
                              {reviewer?.subjectAreas?.length > 3 && (
                                <span className="px-2 py-1 bg-muted text-xs">
                                  +{reviewer?.subjectAreas?.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Icon name="Briefcase" size={14} />
                              {reviewer?.experienceLevel || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="FileText" size={14} />
                              Current workload: {reviewer?.currentWorkload} papers
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-background border-t border-border px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReviewerModal(false);
                  setSelectedReviewer(null);
                  setReviewerSearch('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignReviewer}
                disabled={!selectedReviewer || assigningReviewer}
                iconName={assigningReviewer ? 'Loader' : 'UserPlus'}
                iconPosition="left"
              >
                {assigningReviewer ? 'Assigning...' : t('managementDashboard.submissions.assignReviewer')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenceManagementDashboard;
